import { useCallback, useEffect, useState } from 'react';
import { Badge, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api } from './api.js';
import { money } from './format.js';

/**
 * Fees and liability.
 *
 * The page answers two separate questions and keeps them visually apart,
 * because confusing them is how a platform ends up funding other people's
 * chargebacks by accident:
 *
 *  - what is deducted from a payment, and
 *  - who is out of pocket when something goes wrong.
 *
 * Most of the second part is not configurable. It follows from Direct Charges
 * and from the connected accounts' controller settings, which are fixed when an
 * account is created. Showing it as fact rather than as a greyed-out switch
 * stops anyone hunting for a control that does not exist.
 */

const BEARER_LABEL: Record<string, string> = {
  merchant: 'ТЪРГОВЕЦЪТ',
  platform: 'ПЛАТФОРМАТА',
  stripe: 'STRIPE',
};

const BEARER_TONE: Record<string, 'ok' | 'fail' | 'info'> = {
  merchant: 'ok',
  platform: 'fail',
  stripe: 'info',
};

function Row({ what, who, detail }: { what: string; who: string; detail: string }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(180px, 1fr) auto',
        gap: 12,
        alignItems: 'start',
        padding: '12px 0',
        borderBottom: '1px solid var(--wc-border)',
      }}
    >
      <div>
        <strong style={{ fontSize: 14.5 }}>{what}</strong>
        <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--wc-muted)' }}>{detail}</p>
      </div>
      <Badge tone={BEARER_TONE[who] ?? 'neutral'}>{BEARER_LABEL[who] ?? who}</Badge>
    </div>
  );
}

export function FeesView() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.fees>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.fees());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Note tone="danger">{error}</Note>;
  if (!data) return <Spinner label="Зареждане…" />;

  const { rates, liability } = data;
  const exposed = liability.negativeBalance === 'platform';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Panel title="Какво се удържа от едно плащане">
        <div style={{ display: 'grid', gap: 10 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
            }}
          >
            <Cell
              label="Обработващ (Stripe)"
              value={`${(rates.processorPercentBps / 100).toFixed(2)}%`}
              hint={rates.processorFixed > 0 ? `+ ${money(rates.processorFixed, rates.currency)}` : undefined}
            />
            <Cell
              label="Платформата"
              value={`${(rates.platformPercentBps / 100).toFixed(2)}%`}
              hint={rates.platformFixed > 0 ? `+ ${money(rates.platformFixed, rates.currency)}` : undefined}
              accent="var(--wc-green)"
            />
            <Cell
              label="Общо, както се обявява"
              value={`${(rates.combinedPercentBps / 100).toFixed(2)}%`}
              hint={rates.combinedFixed > 0 ? `+ ${money(rates.combinedFixed, rates.currency)}` : undefined}
              accent="var(--wc-blue)"
              strong
            />
          </div>
        </div>

        <Note title="Обявява се едно число">
          В общите условия и към търговците се пише <strong>{data.combined_label}</strong> - без
          разбивка кое колко е. Разпределението се вижда само тук.
        </Note>

        <Note tone="warn" title="Ставката на Stripe не е фиксирана">
          Числото по-горе е обявената ставка на Stripe и служи само за това общо число. Реалното
          удържане зависи от вида карта и от държавата, издала я - международна карта струва повече
          от местна. Колко точно е струвало конкретно плащане се вижда в детайла му, прочетено от
          Stripe, а не сметнато тук.
        </Note>
      </Panel>

      <Panel title="Кой поема разноските">
        <Row
          what="Рефунд"
          who={liability.refunds}
          detail="Плащането живее в свързания профил на търговеца, така че връщането дебитира неговия баланс."
        />
        <Row
          what="Оспорване (chargeback)"
          who={liability.disputes}
          detail="Оспорената сума и таксата на Stripe за спор се теглят от профила, към който принадлежи плащането."
        />
        <Row
          what="Отрицателен баланс"
          who={liability.negativeBalance}
          detail={
            exposed
              ? 'Ако търговецът не може да покрие дълга, той остава за платформата.'
              : 'Ако търговецът не може да покрие дълга, Stripe го поема. Това следва от начина, по който се създават свързаните профили, и не може да се смени след създаването им.'
          }
        />

        <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
          <Toggle
            label="Таксата на платформата се връща при рефунд"
            on={liability.platformFeeRefunded}
            offMeaning="Таксата остава при платформата. Работата по приемането на плащането е свършена."
            onMeaning="Таксата се връща заедно с рефунда."
          />
          <Toggle
            label="Таксата на платформата се връща при загубен спор"
            on={liability.platformFeeRefundedOnDispute}
            offMeaning="Таксата остава при платформата."
            onMeaning="Таксата се връща и при загубен спор."
          />
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--wc-faint)' }}>
            И двете се сменят от Настройки на платформата - това са подписани команди с втори
            подпис, защото променят колко пари остават при вас.
          </p>
        </div>
      </Panel>

      {exposed ? (
        <Note tone="danger" title="Платформата носи загубите">
          Свързаните профили са създадени така, че при непокрит отрицателен баланс дългът остава за
          Wyoming Cloud Solutions LLC. Ако това не е желаното, новите профили трябва да се създават с
          Stripe като събиращ документите - за вече съществуващи профили не може да се промени.
        </Note>
      ) : null}

      <Panel title="Как е формулирано">
        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8, fontSize: 13.5 }}>
          {liability.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function Cell({
  label,
  value,
  hint,
  accent,
  strong,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        borderLeft: `3px solid ${accent ?? 'var(--wc-border-strong)'}`,
        padding: '8px 0 8px 12px',
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--wc-faint)',
        }}
      >
        {label}
      </div>
      <div className="wc-num" style={{ fontSize: strong ? 26 : 22, fontWeight: strong ? 700 : 600 }}>
        {value}
      </div>
      {hint ? <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{hint}</div> : null}
    </div>
  );
}

function Toggle({
  label,
  on,
  onMeaning,
  offMeaning,
}: {
  label: string;
  on: boolean;
  onMeaning: string;
  offMeaning: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      <Badge tone={on ? 'warn' : 'ok'} icon={false}>
        {on ? 'ВКЛ' : 'ИЗКЛ'}
      </Badge>
      <div>
        <strong style={{ fontSize: 14 }}>{label}</strong>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--wc-muted)' }}>
          {on ? onMeaning : offMeaning}
        </p>
      </div>
    </div>
  );
}
